import { Container, Graphics, Sprite } from 'pixi.js';
import type { Grid, LayoutProfile, MathConfig, ResolvedTheme, SpinPlan } from '../types';
import { alphaOf, toPixiColor } from '../util/color';
import { ReelView } from './reel';
import type { TextureStore } from './textures';
import type { Timeline } from './tween';

/** All reels plus backdrop, frame and separators. Local origin = top-left of the frame. */
export class ReelSet extends Container {
  private readonly math: MathConfig;
  private theme: ResolvedTheme;
  private readonly textures: TextureStore;
  private readonly timeline: Timeline;
  private readonly backdrop = new Graphics();
  private readonly separators = new Graphics();
  private readonly frame = new Graphics();
  private frameSprite: Sprite | null = null;
  private readonly reels: ReelView[] = [];
  private profile: LayoutProfile;
  private spinning = false;
  private landed = new Set<number>();

  constructor(o: { math: MathConfig; theme: ResolvedTheme; textures: TextureStore; timeline: Timeline }) {
    super();
    this.math = o.math;
    this.theme = o.theme;
    this.textures = o.textures;
    this.timeline = o.timeline;
    this.profile = o.theme.layout.portrait;
    this.addChild(this.backdrop, this.separators);
    const L = this.profile.reels;
    for (let i = 0; i < o.math.reels; i++) {
      const reel = new ReelView({
        index: i,
        strip: o.math.strips[i],
        rows: o.math.rows,
        symbolWidth: L.symbolWidth,
        symbolHeight: L.symbolHeight,
        gapY: L.gapY,
        textures: o.textures,
        timeline: o.timeline,
        blurStrength: o.theme.animations.timing.spin.blur,
      });
      this.reels.push(reel);
      this.addChild(reel);
    }
    this.addChild(this.frame);
    this.layout(this.profile);
  }

  get symbolSize(): { w: number; h: number } {
    return { w: this.profile.reels.symbolWidth, h: this.profile.reels.symbolHeight };
  }

  /** Outer size of the frame in local units. */
  get frameSize(): { w: number; h: number } {
    const L = this.profile.reels;
    return {
      w: L.padding * 2 + this.math.reels * L.symbolWidth + (this.math.reels - 1) * L.gapX,
      h: L.padding * 2 + this.math.rows * L.symbolHeight + (this.math.rows - 1) * L.gapY,
    };
  }

  cellPosition(reel: number, row: number): { x: number; y: number } {
    const L = this.profile.reels;
    return {
      x: L.padding + reel * (L.symbolWidth + L.gapX) + L.symbolWidth / 2,
      y: L.padding + row * (L.symbolHeight + L.gapY) + L.symbolHeight / 2,
    };
  }

  layout(profile: LayoutProfile): void {
    this.profile = profile;
    const L = profile.reels;
    this.position.set(L.x, L.y);
    this.reels.forEach((reel, i) => {
      reel.resize(L.symbolWidth, L.symbolHeight, L.gapY);
      reel.position.set(L.padding + i * (L.symbolWidth + L.gapX), L.padding);
    });
    this.draw();
  }

  private draw(): void {
    const p = this.theme.palette;
    const L = this.profile.reels;
    const { w, h } = this.frameSize;
    this.backdrop.clear().roundRect(0, 0, w, h, L.cornerRadius).fill({ color: toPixiColor(p.reelBackdrop), alpha: alphaOf(p.reelBackdrop) });
    this.separators.clear();
    for (let i = 1; i < this.math.reels; i++) {
      const x = L.padding + i * (L.symbolWidth + L.gapX) - L.gapX / 2;
      this.separators.moveTo(x, L.padding).lineTo(x, h - L.padding).stroke({ color: toPixiColor(p.reelSeparator), alpha: alphaOf(p.reelSeparator), width: Math.max(1, L.gapX) });
    }
    const frameUrl = this.theme.assets.reelFrame;
    const frameTex = frameUrl ? this.textures.image(frameUrl) : null;
    this.frame.clear();
    if (frameTex) {
      if (!this.frameSprite) {
        this.frameSprite = new Sprite(frameTex);
        this.addChild(this.frameSprite);
      }
      this.frameSprite.texture = frameTex;
      const pad = L.padding;
      this.frameSprite.position.set(-pad, -pad);
      this.frameSprite.width = w + pad * 2;
      this.frameSprite.height = h + pad * 2;
      this.frameSprite.visible = true;
    } else {
      if (this.frameSprite) this.frameSprite.visible = false;
      this.frame.roundRect(0, 0, w, h, L.cornerRadius).stroke({ color: toPixiColor(p.reelFrame), alpha: alphaOf(p.reelFrame), width: 6, alignment: 0 });
      this.frame.roundRect(-3, -3, w + 6, h + 6, L.cornerRadius + 3).stroke({ color: 0x000000, alpha: 0.25, width: 2 });
    }
  }

  setTheme(theme: ResolvedTheme): void {
    this.theme = theme;
    this.draw();
  }

  setGrid(grid: Grid): void {
    this.reels.forEach((reel, i) => reel.setColumn(grid[i]));
    this.spinning = false;
  }

  reel(i: number): ReelView {
    return this.reels[i];
  }

  /** Animate all reels to the planned grid. Resolves when every reel landed and minDurationMs elapsed. */
  async spin(plan: SpinPlan, quick: boolean, onReelStop: (reel: number) => void): Promise<void> {
    const T = this.theme.animations.timing;
    const first = quick ? T.quickSpin.firstStopMs : T.spin.firstStopMs;
    const stagger = quick ? T.quickSpin.staggerMs : T.spin.staggerMs;
    const ant = plan.anticipationFromReel;
    const glow = toPixiColor(this.theme.palette.anticipationGlow);
    this.spinning = true;
    this.landed.clear();
    const spins = this.reels.map((reel, i) => {
      const anticipation = ant != null && i >= ant && this.theme.animations.effects.anticipationGlow;
      const extra = ant != null && i >= ant ? (i - ant + 1) * T.anticipation.extraMsPerReel : 0;
      return reel.spinTo(plan.book.grid[i], {
        stopAtMs: first + i * stagger + extra,
        timing: T.spin,
        quick,
        anticipation,
        glowColor: glow,
        onStop: () => {
          this.landed.add(i);
          onReelStop(i);
        },
      });
    });
    await Promise.all([...spins, this.timeline.wait(Math.max(0, plan.minDurationMs))]);
    this.spinning = false;
  }

  quickStop(): void {
    if (!this.spinning) return;
    for (const reel of this.reels) {
      if (!this.landed.has(reel.index)) {
        reel.setGlow(null);
        reel.landNow();
      }
    }
  }

  override destroy(): void {
    super.destroy({ children: true });
  }
}
